import { ratesLabels } from 'axl-config';
import { formatCurrency, formatNumber } from '@atc/string-fns';
import calculatePersonalPayment from '../calculatePersonalPayment/calculatePersonalPayment.js';
import getLowestPersonalizedPayment from '../getLowestPersonalizedPayment/getLowestPersonalizedPayment.js';
import getObfuscatedCredit from '../getObfuscatedCredit/getObfuscatedCredit.js';

/**
 * Formats a number with a fallback value if the input is invalid or does not meet certain conditions.
 *
 * @param {number} value - The number to format.
 * @param {boolean} [allowZero=false] - Whether to allow zero as a valid value.
 * @param {boolean} [isCurrency=false] - Whether to format the number as currency.
 * @param {string} [fallback='—'] - The fallback value to return if the input is invalid.
 * @returns {string} - The formatted number as a string, or the fallback value if the input is invalid.
 */
const formatNumberWithFallback = function (value) {
  let allowZero = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let isCurrency = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  let fallback = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '—';
  if (typeof value !== 'number' || Number.isNaN(value) || !allowZero && !value) {
    return fallback;
  }
  if (isCurrency) {
    return formatCurrency(value).toString();
  }
  return formatNumber({
    value
  }).toString();
};

/**
 * Generates deal information, summary, and estimates based on provided pricing, payment, and deal details.
 *
 * @param {Object} [pricingDetail={}] - Pricing details of the vehicle.
 * @param {Object} [paymentDetails={}] - Payment details including offer price, lender info, and taxes/fees.
 * @param {string} [type='used'] - Type of the vehicle (e.g., 'used', 'certified').
 * @param {Object} [payments={}] - Payment-related details including trade-in, down payment, and credit verification.
 * @param {boolean} [isNDSCalc=false] - Indicates if NDS (National Dealer Services) calculation is used.
 * @param {boolean} [isNationalRates=false] - Indicates if national rates are used.
 * @param {boolean} [disableTrade=false] - Indicates if trade-in details should be excluded.
 * @returns {Object} - An object containing:
 *   - `deal` (Object): The deal information including sale price, down payment, trade-in details, and financing/lease specifics.
 *   - `summary` (Array): A summary of the deal with labels and values for display.
 *   - `estimates` (Object): Estimated payment details for email and opt-in purposes.
 */
const getDealInfo = function () {
  let pricingDetail = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let paymentDetails = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'used';
  let payments = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  let isNDSCalc = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  let isNationalRates = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  let disableTrade = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  const {
    creditType,
    downPayment = 0,
    interestRate = {},
    loanTerm,
    leaseTerm,
    leaseMileageRange = null,
    tradeIn = {},
    icoTradeIn = {},
    dealType = '',
    creditVerification
  } = payments;
  const {
    amountOwedPayoffDetails,
    amountOwedSource
  } = tradeIn;
  const creditCustomerReport = creditVerification?.customerReport || '';
  const decodedCreditType = ratesLabels[creditType]?.decodedValue;
  const decodedCreditRating = ratesLabels[creditVerification?.creditRating]?.decodedValue;
  const hasPayoff = amountOwedSource || amountOwedPayoffDetails;
  const isCashDeal = dealType?.toLowerCase() === 'cash';
  const isLeaseDeal = dealType?.toLowerCase() === 'lease';
  const isFinanceDeal = dealType?.toLowerCase() === 'finance';
  const icoOffer = {
    ...tradeIn,
    value: icoTradeIn?.value
  };

  // Display or pass downPayment as '0' when its Null;
  const downPaymentValue = downPayment || 0;
  const selectedTradeIn = Object.keys(icoTradeIn).length ? icoOffer : tradeIn;
  payments = {
    ...payments,
    tradeIn: selectedTradeIn
  };
  const {
    value = 0,
    amountOwed = 0,
    year = '',
    make = '',
    model = ''
  } = selectedTradeIn;
  const {
    offerPrice = '',
    icoTradeInOffer = '',
    customLender = {},
    downPayment: leaseDownPayment = 0,
    capCost = {},
    cashDealPrice = null
  } = paymentDetails;
  const {
    adjustedCapCost
  } = capCost;
  const personalizedPayment = getLowestPersonalizedPayment(paymentDetails, getObfuscatedCredit(creditType), loanTerm);
  const {
    taxesFees = {}
  } = isFinanceDeal ? personalizedPayment : paymentDetails;
  const taxesAndFees = Math.floor(taxesFees?.totalTaxesFees ?? 0);
  const totalTaxes = Math.floor(taxesFees?.totalTax ?? 0);
  const dealerFees = Math.floor(taxesFees?.dealerFees ?? 0);
  const dmvFees = Math.floor(taxesFees?.dmvFees ?? 0);
  const lenderFees = Math.floor(taxesFees?.lenderFees ?? 0);
  const tradeInValue = Math.max(0, isNDSCalc && icoTradeInOffer ? icoTradeInOffer : value);
  let vehicleCondition = type.toLowerCase();
  if (vehicleCondition === 'certified') vehicleCondition = 'used';
  let price = offerPrice || pricingDetail.salePrice || pricingDetail.msrp;
  if (!offerPrice && isCashDeal && cashDealPrice) {
    // Applying dealer setting for cash price.
    price = cashDealPrice;
  }
  const totalEquity = tradeInValue - amountOwed;
  let totalCashOut = price - totalEquity;
  let amountFinanced = price - totalEquity - downPaymentValue;
  if (taxesAndFees) {
    // Applying taxes and fee for final price
    amountFinanced += taxesAndFees;
    totalCashOut += taxesAndFees;
  }
  const customInterestRate = interestRate?.custom >= 0 ? interestRate?.custom : null;

  // Lender, Rate, Payment assignments
  let lender;
  let rate;
  let payment;

  // Use custom interest rate if set and calculate payment,
  // otherwise override with Custom Lender info if provided,
  // or personalized payments, if available,
  // finally fall back to national average with calculated payment
  if (isLeaseDeal) {
    payment = paymentDetails?.payment;
    lender = null;
    rate = paymentDetails?.[vehicleCondition] ?? null;
  } else if (typeof customInterestRate === 'number') {
    lender = 'Custom Rate';
    rate = customInterestRate;
    payment = calculatePersonalPayment(price, payments, customInterestRate);
  } else if (customLender?.payment) {
    ({
      payment,
      lender,
      rate
    } = customLender);
  } else if (Object.keys(personalizedPayment).length) {
    // Use Personalized Payments
    ({
      payment,
      offeredBy: lender
    } = personalizedPayment);
    rate = personalizedPayment[vehicleCondition];
  } else {
    // Use National Average
    lender = 'National Average Rate';
    rate = !!interestRate && !!Object.keys(interestRate).length ? interestRate[vehicleCondition] : 4.5;
    payment = calculatePersonalPayment(price, payments, rate);
  }
  const formattedPayment = formatNumberWithFallback(Math.max(0, Math.floor(payment)), true, false, '');

  // base cash option deal object for email lead
  let deal = {
    dealType,
    salePrice: formatNumberWithFallback(price, false, false, ''),
    customPrice: !!offerPrice,
    downPayment: formatNumberWithFallback(isCashDeal ? totalCashOut : downPaymentValue, true, false, ''),
    customReport: decodedCreditType === decodedCreditRating ? creditCustomerReport : '',
    creditRange: ratesLabels[creditType?.toString()]?.decodedValue
  };
  if (!disableTrade) {
    deal.tradeInValue = formatNumberWithFallback(tradeInValue, true, false, '');
    deal.tradeInEquity = formatNumberWithFallback(totalEquity, true, false, '');
    deal.tradeInPayoff = formatNumberWithFallback(amountOwed, true, false, '');
    deal.instantCashOffer = !!(isNDSCalc && icoTradeInOffer);
  }

  // financing deal object for email lead
  if (isFinanceDeal) {
    deal = {
      ...deal,
      amountFinanced: formatNumberWithFallback(amountFinanced, true, false, ''),
      terms: loanTerm?.toString(),
      rate: rate?.toString(),
      lender: lender?.toString(),
      payment: formattedPayment
    };
    if (!disableTrade) {
      deal.tradeInYear = year?.toString();
      deal.tradeInMake = make?.toString();
      deal.tradeInModel = model?.toString();
    }
  } else if (isLeaseDeal) {
    // lease deal object for email lead
    // we send both downPayment and leaseDownPayment
    deal = {
      ...deal,
      terms: leaseTerm?.toString(),
      ...(!disableTrade && {
        tradeInValue: formatNumberWithFallback(tradeInValue, true, false, '')
      }),
      mileageLimit: formatNumberWithFallback(parseInt(leaseMileageRange, 10), true, false, ''),
      adjustedCapCost: formatNumberWithFallback(Math.max(0, Math.floor(adjustedCapCost)), true, false, 'N/A'),
      leaseDownPayment: formatNumberWithFallback(leaseDownPayment, false, false, 'N/A'),
      payment: formatNumberWithFallback(Math.max(0, Math.floor(payment)), true, false, 'N/A'),
      rate: rate?.toString()
    };
  } else if (isCashDeal) {
    deal = {
      ...deal,
      estCashOutOfPocket: formatNumberWithFallback(totalCashOut, true, false, '')
    };
  }
  const hasInvalidLeaseDeal = isLeaseDeal && deal.payment === 'N/A';

  // wallet summary + estimates arrays supplied to WalletSnapshot and WalletOptIn components
  const summary = hasInvalidLeaseDeal ? [{
    label: `${offerPrice ? 'My Offer' : 'Listing Price'}`,
    value: formatNumberWithFallback(price, false, true),
    isTotalValue: false
  }, {
    label: 'Cash Down',
    value: formatNumberWithFallback(downPaymentValue, true, true),
    isTotalValue: false
  }] : [{
    label: `${offerPrice ? 'My Offer' : 'Listing Price'}`,
    value: formatNumberWithFallback(price, false, true),
    isTotalValue: false
  }, ...(disableTrade ? [] : [{
    label: 'Trade-In Value',
    value: formatNumberWithFallback(tradeInValue ?? 0, true, true),
    isTotalValue: false
  }]), ...(disableTrade ? [] : [{
    label: 'Amount Owed on Trade-In',
    value: formatNumberWithFallback(amountOwed ?? 0, true, true),
    isTotalValue: false
  }])];
  const creditRatingsLabels = creditValue => {
    const creditTypesValues = ratesLabels[creditValue];
    return creditTypesValues ? `${creditTypesValues.label} ${creditTypesValues.subLabel}` : '—';
  };
  const estimates = {
    email: [{
      label: 'Estimated Payment',
      value: formattedPayment ? `$${formattedPayment}/mo` : '—',
      isTotalValue: false
    }],
    // static as dealType doesn't apply outside of NDS Calc
    optIn: [{
      label: 'Down Payment',
      value: formatNumberWithFallback(downPaymentValue ?? 0, true, true),
      isTotalValue: false
    }, ...(disableTrade ? [] : [{
      label: 'Est. Trade-In Value',
      value: formatNumberWithFallback(tradeInValue ?? 0, true, true),
      isTotalValue: false
    }, {
      label: 'Amount Owed on Trade',
      value: formatNumberWithFallback(amountOwed ?? 0, true, true),
      isTotalValue: false
    }]), {
      label: 'Credit Rating',
      value: creditRatingsLabels(creditType),
      isTotalValue: false
    }, {
      label: 'Term Length',
      value: `${loanTerm} Months`,
      isTotalValue: false
    }]
  };
  if (isFinanceDeal) {
    summary.push({
      label: 'Down Payment',
      value: formatNumberWithFallback(downPaymentValue ?? 0, true, true),
      isTotalValue: false
    }, {
      label: `${amountFinanced <= 0 ? 'Estimated Cash Out of Pocket' : 'Amount Financed'}`,
      value: formatNumberWithFallback(Math.max(0, amountFinanced), true, true),
      isTotalValue: true
    });
    estimates.email.unshift({
      label: 'Lender',
      value: lender,
      isTotalValue: false
    }, {
      label: 'Rate',
      value: `${rate}%`,
      isTotalValue: false
    }, {
      label: 'Credit Rating',
      value: creditRatingsLabels(creditType),
      isTotalValue: false
    }, {
      label: 'Term Length',
      value: `${loanTerm} Months`,
      isTotalValue: false
    });
  }
  if (isCashDeal) {
    summary.push({
      label: 'Estimated Cash Payment',
      value: formatNumberWithFallback(Math.max(0, totalCashOut), true, true),
      isTotalValue: true
    });
  }
  if (isLeaseDeal) {
    if (!hasInvalidLeaseDeal) {
      summary.push({
        label: 'Est. Adjusted Cap Cost',
        value: formatNumberWithFallback(Math.max(0, Math.floor(adjustedCapCost)), true, true),
        isTotalValue: true
      });
    }
    estimates.email.unshift({
      label: 'Due At Signing',
      value: formatNumberWithFallback(leaseDownPayment, true, true),
      isTotalValue: false
    }, {
      label: 'Miles Per Year',
      value: formatNumberWithFallback(leaseMileageRange, false),
      isTotalValue: false
    }, {
      label: 'Lease Term',
      value: `${leaseTerm} Months`,
      isTotalValue: false
    });
  }
  if (taxesAndFees && summary.length) {
    // Applying taxes and fee for summary and email leads
    if (!hasInvalidLeaseDeal) {
      summary.splice(summary.length - 1, 0, {
        label: 'Taxes & Fees',
        value: formatNumberWithFallback(taxesAndFees, true, true),
        isTotalValue: false
      });
    }
    deal.taxesAndFees = formatNumberWithFallback(taxesAndFees, true, false, '');
    deal.totalTaxes = formatNumberWithFallback(totalTaxes, true, false, '');
    deal.fees = [{
      amount: dmvFees,
      name: 'DMV FEE'
    }, {
      amount: dealerFees,
      name: 'DEALER FEE'
    }, {
      amount: lenderFees,
      name: 'LENDER FEE'
    }];
  }
  if (isNationalRates && !taxesAndFees && summary.length && !hasInvalidLeaseDeal) {
    summary.splice(summary.length - 1, 0, {
      label: 'Taxes & Fees',
      value: 'N/A',
      isTotalValue: false
    });
  }
  if (hasPayoff && !disableTrade) {
    const {
      financeMethod,
      lender: tradeInLender,
      goodThroughDate
    } = amountOwedPayoffDetails || {};
    deal.tradeInPayoffAmountOwed = amountOwed;
    deal.tradeInPayoffAmountOwedSource = amountOwedSource;
    deal.tradeInPayoffFinanceMethod = financeMethod;
    deal.tradeInPayoffGoodThroughDate = goodThroughDate;
    deal.tradeInPayoffLenderName = tradeInLender?.name;
  }
  return {
    deal,
    summary,
    estimates
  };
};
export { getDealInfo as default };
